/** Coerce API values that may be arrays, maps, or single objects into a real array. */
export const ensureArray = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        return ensureArray(JSON.parse(trimmed));
      } catch {
        return [];
      }
    }
    return [];
  }

  if (typeof value !== "object") return [];

  if (
    value.pathSegments ||
    value.path_segments ||
    value.routeCarrierCodes ||
    value.route_carrier_codes ||
    value.rateCarrierCodes ||
    value.rate_carrier_codes ||
    value.carrier_code ||
    value.carrierCode
  ) {
    return [value];
  }

  const values = Object.values(value);
  if (values.length && values.every((item) => item != null && typeof item === "object")) {
    return values;
  }

  return [];
};

const normalizeBreakdowns = (routeRate) => {
  if (!routeRate || typeof routeRate !== "object") return routeRate;

  return {
    ...routeRate,
    rateBreakdown: ensureArray(routeRate.rateBreakdown || routeRate.rate_breakdown),
    accBreakdown: ensureArray(routeRate.accBreakdown || routeRate.acc_breakdown),
    adjustedBreakdown: ensureArray(
      routeRate.adjustedBreakdown || routeRate.adjusted_breakdown
    ),
  };
};

const normalizeSegment = (segment) => {
  if (!segment || typeof segment !== "object") return segment;

  const routeRate = segment.routeRate || segment.route_rate;
  return {
    ...segment,
    routeCarrierCode: segment.routeCarrierCode || segment.route_carrier_code,
    routeRate: normalizeBreakdowns(routeRate),
  };
};

const ensureCodeList = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const normalizePathRow = (row) => {
  if (!row || typeof row !== "object") return row;

  const pathSegments = ensureArray(row.pathSegments || row.path_segments).map(
    normalizeSegment
  );

  return {
    ...row,
    pathSegments,
    routeCarrierCodes: ensureCodeList(
      row.routeCarrierCodes || row.route_carrier_codes || row.route_carrier_code
    ),
    rateCarrierCodes: ensureCodeList(
      row.rateCarrierCodes || row.rate_carrier_codes || row.rate_carrier_code
    ),
    routeCarrierNames: ensureArray(row.routeCarrierNames || row.route_carrier_names),
    pathTotalCost: row.pathTotalCost ?? row.path_total_cost,
    adjustedPathTotalCost: row.adjustedPathTotalCost ?? row.adjusted_path_total_cost,
    pathTotalPrice: row.pathTotalPrice ?? row.path_total_price,
    adjustedPathTotalPrice: row.adjustedPathTotalPrice ?? row.adjusted_path_total_price,
    pathTotalTime: row.pathTotalTime ?? row.path_total_time,
  };
};

export const extractRankedCarriers = (data) => {
  if (!data || typeof data !== "object") return [];
  return ensureArray(data.ranked_carriers || data.rankedCarriers);
};

export const extractAllocationMeta = (data) => {
  if (!data || typeof data !== "object") {
    return { laneKey: null, allocationMode: null, laneTotalAllocations: null };
  }
  const laneTotal = data.lane_total_allocations ?? data.laneTotalAllocations;
  return {
    laneKey: data.lane_key ?? data.laneKey ?? null,
    allocationMode: data.allocation_mode ?? data.allocationMode ?? null,
    laneTotalAllocations:
      laneTotal != null && laneTotal !== "" ? Number(laneTotal) : null,
  };
};

/** Map ranked carrier rows by carrier code (includes PR #994 allocation fields). */
export const buildRankedCarrierMap = (rankedCarriers = []) => {
  const map = new Map();
  rankedCarriers.forEach((row, index) => {
    const code = (row?.carrier_code || row?.carrierCode || "")
      .toString()
      .trim()
      .toUpperCase();
    if (!code) return;

    const num = (v) => {
      if (v == null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const rank = row?.rank != null ? Number(row.rank) : index + 1;
    map.set(code, {
      rank: Number.isFinite(rank) ? rank : index + 1,
      price: num(row?.price),
      cost: num(row?.cost),
      score: num(row?.score),
      kpiFactor: num(row?.kpi_factor ?? row?.kpiFactor),
      targetAllocationPct: num(row?.target_allocation_pct ?? row?.targetAllocationPct),
      actualAllocationPct: num(row?.actual_allocation_pct ?? row?.actualAllocationPct),
      allocationCount: num(row?.allocation_count ?? row?.allocationCount),
      allocationDeficit: num(row?.allocation_deficit ?? row?.allocationDeficit),
      weightedFirstPick: row?.weighted_first_pick ?? row?.weightedFirstPick ?? null,
    });
  });
  return map;
};

const extractPaths = (data) => {
  if (!data || typeof data !== "object") return [];

  const candidates = [
    data.route_solutions,
    data.routeSolutions,
    data.paths,
    data.results,
    data.content,
    data.data,
  ];

  for (const candidate of candidates) {
    const rows = ensureArray(candidate);
    if (rows.length) return rows;
  }

  if (data.pathSegments || data.path_segments) {
    return [data];
  }

  return [];
};

const emptyRateShoppingParse = () => ({
  paths: [],
  rankedCarriers: [],
  outcomeId: null,
  allocationMeta: extractAllocationMeta(null),
});

/** API may return a bare array or a wrapped object — always coerce path rows to an array. */
export const parseRateShoppingResponse = (data, { extractOutcomeId } = {}) => {
  if (Array.isArray(data)) {
    return {
      paths: data.map(normalizePathRow),
      rankedCarriers: [],
      outcomeId: null,
      allocationMeta: extractAllocationMeta(null),
    };
  }

  if (typeof data === "string") {
    return parseRateShoppingResponse(ensureArray(data)[0] ?? null, { extractOutcomeId });
  }

  if (!data || typeof data !== "object") {
    return emptyRateShoppingParse();
  }

  if (
    data.data &&
    typeof data.data === "object" &&
    !Array.isArray(data.data) &&
    (data.data.route_solutions ||
      data.data.routeSolutions ||
      data.data.ranked_carriers ||
      data.data.rankedCarriers ||
      data.data.paths ||
      data.data.results)
  ) {
    return parseRateShoppingResponse(data.data, { extractOutcomeId });
  }

  const rankedCarriers = extractRankedCarriers(data);
  const paths = extractPaths(data).map(normalizePathRow);
  const outcomeId =
    typeof extractOutcomeId === "function" ? extractOutcomeId(data) : null;

  return {
    paths,
    rankedCarriers,
    outcomeId,
    allocationMeta: extractAllocationMeta(data),
  };
};

export const normalizeRateShoppingResults = (data) =>
  ensureArray(parseRateShoppingResponse(data).paths);
